import styled from '@emotion/styled';
import { TooltipInlineBlock } from '@txt/core.ui/tooltip';
import classNames from 'classnames';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { displayCurrency, RoundingMode } from '../displayCurrency';
import { formatMessage, FormattedMessage } from '../i18n';

export type Props = {
  /** Price in USD required. */
  price: number;
  currency: string;
  rounding?: RoundingMode;
  showSymbol?: boolean;
  className?: string;
  perMonth?: boolean;
};

/**
 * Renders a price value wrapped with a tooltip that displays the original source value (USD).
 */
export const FormattedPrice: React.FC<Props> = ({ price, currency, perMonth, rounding, showSymbol, className }) => {
  return (
    <Styles.Root className={classNames('formattedPrice', className)}>
      <span>
        {displayCurrency({
          value: price,
          currency,
          showSymbol,
          rounding,
        })}
      </span>
      {perMonth && <FormattedMessage id="price.perMonth" />}
    </Styles.Root>
  );
};

type UnknownPriceProps = {
  currency: string;
  content?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

export const UnknownPrice: React.FC<UnknownPriceProps> = ({ currency, children, content, className }) => {
  const intl = useIntl();

  const currencySymbol = React.useMemo(
    () => intl.formatNumberToParts(0, { style: 'currency', currency }).find((part) => part.type === 'currency')?.value,
    [intl, currency],
  );

  return (
    <TooltipInlineBlock content={content ?? <FormattedMessage id="price.unknown" />} place="top" className={className}>
      <span>{children ?? `${currencySymbol}?`}</span>
    </TooltipInlineBlock>
  );
};

/**
 * Render a price value as string, perfect for svg or charts, otherwise, always use the above FormattedPrice!
 */
export const formattedPrice = ({
  price,
  perMonth,
  rounding,
  showSymbol,
  currency,
  compact,
}: Props & { currency: string; compact?: (convertedPrice: number) => boolean }) => {
  const result = displayCurrency({
    value: price,
    currency,
    rounding,
    compact: compact?.(price),
    showSymbol,
  });

  if (perMonth) {
    return `${result}${formatMessage('price.perMonth')}`;
  }

  return result;
};

const Styles = {
  Root: styled.div`
    display: inline-block;
  `,
};
